import { defineStore } from "pinia";
import { ref } from "vue";
import { useBase } from "@/utils/stores/base";
import { usePagination } from "@/utils/stores/pagination";
import { useFiltering } from "@/utils/stores/filtering";


export const useRiskScoring = (ownerResourceType, ownerResourceId) => {
  const path = `${ownerResourceType}/${ownerResourceId}/risk-scorings`;
  const includes = ["reviewer"];
  const storeName = () => {
    if (ownerResourceType == "physical-persons") {
      return "physicalPersonRiskScoring";
    } else if (ownerResourceType === "companies") {
      return "companyRiskScoring";
    } else if (ownerResourceType === "sale-listing-agreements") {
      return "saleListingAgreementRiskScoring";
    } else if (ownerResourceType === "rental-listing-agreements") {
      return "rentalListingAgreementRiskScoring";
    }
  };
  return defineStore(storeName() + ownerResourceId, () => {
    const all = ref([]);
    const latest = ref(null);
    const searchQuery = ref(null);

    const { sortedResources } = useFiltering(all, searchQuery, "id");
    const { paginatedResources, goToPage, goToNextPage, goToPreviousPage, paginationRange, totalPages, currentPage } = usePagination(sortedResources, 10);
    const { baseFetchOne, baseFetchAll, baseSave, watchRemoteResourceChange, basePost, basePut } = useBase();

    const empty = {};

    const save = async (resource, callback, errorCallback) => {
      return await baseSave(path, resource, callback, errorCallback);
    };

    const fetchAll = async (errorCallback) => {
      all.value = await baseFetchAll(path, errorCallback, includes);
      if (all.value.length > 0) {
        latest.value = all.value[0];
      } else {
        latest.value = null;
      }
    };

    const fetchOne = async (id, errorCallback) => {
      return await baseFetchOne(path, id, errorCallback, includes);
    };

    const skipRiskScoringRuleExecutions = async (riskScoringRuleExecutions, callback, errorCallback) => {
      // Should implement batch call
      for (const riskScoringRuleExecution of riskScoringRuleExecutions) {
        const riskScoringRuleId = riskScoringRuleExecution.relationships.riskScoringRule?.data?.id;
        const skipReason = riskScoringRuleExecution.attributes.skipped ? riskScoringRuleExecution.attributes.skipReason : null;
        const skipDigest = riskScoringRuleExecution.attributes.skipped ? riskScoringRuleExecution.attributes.digest : null;

        const path = `${ownerResourceType}/${ownerResourceId}/risk-scoring-rules/${riskScoringRuleId}/skip-execution`;
        basePut(path, {attributes: {skipReason: skipReason, skipDigest: skipDigest}}, callback, errorCallback);
      }
    };

    const trigger = async (callback, errorCallback) => {
      basePost(`${ownerResourceType}/${ownerResourceId}/risk-scoring/trigger`, {}, callback, errorCallback);
      await fetchAll();
    };

    const confirm = async (riskScoringId, callback, errorCallback) => {
      await basePost(path + `/${riskScoringId}/review`,  {attributes: {reviewApproval: true, reviewComment: ""}}, callback, errorCallback);
      await fetchAll();
    };

    const load = async () => {
      await fetchAll();
      await watchRemoteResourceChange(storeName(), () => {
        fetchAll();
      });
    };

    return {
      all,
      latest,
      currentPage,
      empty,
      load,
      fetchAll,
      fetchOne,
      goToNextPage,
      goToPage,
      goToPreviousPage,
      paginated: paginatedResources,
      paginationRange,
      save,
      searchQuery,
      sorted: sortedResources,
      totalPages,
      skipRiskScoringRuleExecutions,
      confirm,
      trigger
    };
  })();
};
